import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Get token from sessionStorage
const getToken = () => {
  const storedUser = sessionStorage.getItem("loggedUser");
  if (!storedUser) {
    throw new Error('No authentication token found');
  }
  
  const parsedUser = JSON.parse(storedUser);
  const token = parsedUser?.token || parsedUser?.data?.token || parsedUser?.user?.token;
  
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return token;
};

// Fetch delivery withdrawals
export const fetchDeliveryWithdrawals = createAsyncThunk(
  'deliveryWithdrawals/fetchDeliveryWithdrawals',
  async (_, thunkAPI) => {
    try {
      const token = getToken();
      const response = await axios.get(`${BASE_URL}/dashboard/withdrawal_delivery`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

// Update delivery withdrawal status
export const updateDeliveryWithdrawalStatus = createAsyncThunk(
  'deliveryWithdrawals/updateDeliveryWithdrawalStatus',
  async ({ id, status }, thunkAPI) => {
    try {
      const token = getToken();
      const response = await axios.post(`${BASE_URL}/dashboard/withdrawal_delivery/${id}`, { status }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

// Delete delivery withdrawal
export const deleteDeliveryWithdrawal = createAsyncThunk(
  'deliveryWithdrawals/deleteDeliveryWithdrawal',
  async (id, thunkAPI) => {
    try {
      const token = getToken();
      const response = await axios.delete(`${BASE_URL}/dashboard/withdrawal_delivery/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

const initialState = {
  withdrawals: [],
  isLoading: false,
  error: null,
  success: null,
  updateLoading: false,
  deleteLoading: false
};

const deliveryWithdrawalSlice = createSlice({
  name: 'deliveryWithdrawals',
  initialState,
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch delivery withdrawals
      .addCase(fetchDeliveryWithdrawals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryWithdrawals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.withdrawals = action.payload.data;
        state.error = null;
      })
      .addCase(fetchDeliveryWithdrawals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || action.payload || 'حدث خطأ في تحميل طلبات التسوية';
      })
      
      // Update delivery withdrawal status
      .addCase(updateDeliveryWithdrawalStatus.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateDeliveryWithdrawalStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.success = action.payload.message || 'تم تحديث حالة الطلب بنجاح';
        state.error = null;
        // Update the withdrawal in the list
        const index = state.withdrawals.findIndex(w => w.id === action.payload.data.id);
        if (index !== -1) {
          state.withdrawals[index] = action.payload.data;
        }
      })
      .addCase(updateDeliveryWithdrawalStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload?.message || action.payload || 'حدث خطأ في تحديث حالة الطلب';
        state.success = null;
      })
      
      // Delete delivery withdrawal
      .addCase(deleteDeliveryWithdrawal.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteDeliveryWithdrawal.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.success = action.payload.message || 'تم حذف الطلب بنجاح';
        state.error = null;
        // Remove the withdrawal from the list
        state.withdrawals = state.withdrawals.filter(w => w.id !== action.payload.data.id);
      })
      .addCase(deleteDeliveryWithdrawal.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload?.message || action.payload || 'حدث خطأ في حذف الطلب';
        state.success = null;
      });
  },
});

export const { clearState } = deliveryWithdrawalSlice.actions;
export default deliveryWithdrawalSlice.reducer;
